import { Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useMemo, useState } from "react";
import { FiArrowRight, FiBriefcase } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import styles from "./TenantSelectPage.module.css";
import { discoverTenantsRequest } from "../../../services/auth";
import { buildTenantLoginUrl } from "../../../utils/host";

const TenantSelectPage = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [error, setError] = useState("");

  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .email(t("validation.emailFormat"))
          .required(t("validation.emailRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  const onSubmit = async ({ email }) => {
    try {
      setError("");
      const trimmed = email.trim();
      const result = await discoverTenantsRequest(trimmed);
      setSubmittedEmail(trimmed);
      setTenants(Array.isArray(result) ? result : []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || t("auth.tenantSelect.genericError"));
    }
  };

  const handlePick = (slug) => {
    window.location.href = buildTenantLoginUrl(slug, submittedEmail);
  };

  return (
    <div className={styles.wrapper}>
      <Link to="/">
        <img src="/gredus-logo.svg" alt="logo" className={styles.logo} />
      </Link>

      <div className={styles.card}>
        <h2 className={styles.title}>{t("auth.loginCardTitle")}</h2>

        {tenants === null && (
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {error && <p className={styles.error}>{error}</p>}

            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.emailLabel")}</label>
              <input
                type="email"
                autoFocus
                {...register("email")}
                className={styles.input}
              />
              {errors.email && <p className={styles.error}>{errors.email.message}</p>}
            </div>

            <button type="submit" disabled={isSubmitting} className={styles.submitBtn}>
              {isSubmitting ? t("auth.tenantSelect.checking") : t("auth.tenantSelect.continue")}
            </button>
          </form>
        )}

        {tenants !== null && tenants.length === 0 && (
          <div>
            <p className={styles.muted}>
              {t("auth.tenantSelect.noTenantsFound")}
            </p>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => {
                setTenants(null);
                setSubmittedEmail("");
              }}
            >
              {t("auth.tenantSelect.back")}
            </button>
          </div>
        )}

        {tenants !== null && tenants.length > 0 && (
          <div>
            <div className={styles.pickHeader}>
              <h3 className={styles.pickTitle}>{t("auth.tenantSelect.pickTitle")}</h3>
              <p className={styles.pickSubtitle}>
                <span className={styles.emailChip}>{submittedEmail}</span> {t("auth.tenantSelect.pickSubtitleSuffix")}
              </p>
            </div>
            <ul className={styles.tenantList}>
              {tenants.map((t2) => {
                const initial = (t2.name || t2.slug || "?").trim().charAt(0).toUpperCase();
                return (
                  <li key={t2.id}>
                    <button
                      type="button"
                      className={styles.tenantItem}
                      onClick={() => handlePick(t2.slug)}
                    >
                      <span className={styles.tenantAvatar} aria-hidden="true">{initial}</span>
                      <span className={styles.tenantText}>
                        <span className={styles.tenantName}>{t2.name}</span>
                        <span className={styles.tenantSlug}>
                          <FiBriefcase size={12} className={styles.slugIcon} />
                          {t2.slug}
                        </span>
                      </span>
                      <FiArrowRight className={styles.tenantArrow} size={18} />
                    </button>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => {
                setTenants(null);
                setSubmittedEmail("");
              }}
            >
              {t("auth.tenantSelect.useDifferentEmail")}
            </button>
          </div>
        )}

        <p className={styles.registerText}>
          {t("auth.noAccount")}{" "}
          <Link to="/register" className={styles.registerLink}>
            {t("auth.registerPrompt")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default TenantSelectPage;
